import { createSelector } from 'reselect';
import { matchPath } from 'react-router-dom';

import _flatten from 'lodash/flatten';
import _isEmpty from 'lodash/isEmpty';

import { getRouterCurrentStateSelector } from 'store/services/router/selectors';

import * as contextsSelectors from 'store/services/contexts/selectors';
import * as testSetsSelectors from 'store/services/testSets/selectors';
import * as testSetEntriesSelectors from 'store/services/testSetEntries/selectors';
import * as annotationsSelectors from 'store/services/annotations/selectors';

export const getAnnotations = ({ annotations }) =>
  annotations.list.data.results;

export const getHasAnnotations = createSelector(
  getAnnotations,
  annotations => annotations.length > 0,
);

export const getMatchedRulesMap = ({ annotations }) =>
  annotations.list.data.matched_rules_map;

export const getMatchedRulesMapIds = createSelector(
  getMatchedRulesMap,
  match => Object.keys(match),
);

export const getIsLoading = ({ annotations }) => annotations.list.isLoading;

export const getIsLoaded = ({ annotations }) => annotations.list.isLoaded;

export const getUserRanAnnotations = ({ annotations }) =>
  annotations.list.byUser;

// FIXME: Instead of doing this initialize TestSets component with
//        flow prop dependently on scene

export const getAnnotationFlow = createSelector(
  getRouterCurrentStateSelector,
  pathname => {
    if (matchPath(pathname, { path: '/:company/:project/groups' })) {
      return 'context_rules';
    }

    if (matchPath(pathname, { path: '/:company/:project/entities' })) {
      return 'entities';
    }

    if (matchPath(pathname, { path: '/:company/:project/intents' })) {
      return 'intents';
    }

    if (matchPath(pathname, { path: '/:company/:project/dictionaries' })) {
      return 'dictionaries';
    }

    return null;
  },
);

export const getAllMatchedAnnotations = createSelector(
  getAnnotations,
  annotations =>
    _flatten(annotations.map(t => t.annotations))
      .filter((x, i, a) => a.findIndex(y => y.id === x.id) === i)
      .filter(a => a.name),
);

export const getSelectedMatchedAnnotations = createSelector(
  getAnnotations,
  list =>
    list.reduce((ret, item) => {
      const annotations = item.annotations.filter(a => !a.excluded);

      return annotations.length > 0 ? [...ret, { ...item, annotations }] : ret;
    }, []),
);

export const getSelectedAnnotatorResult = ({ annotations }) =>
  annotations.show.data;

export const getHasSelectedAnnotatorResult = createSelector(
  getSelectedAnnotatorResult,
  annotator => !_isEmpty(annotator),
);

export const getAnnotatorParams = createSelector(
  testSetsSelectors.getTestSet,
  testSetEntriesSelectors.getTestSetEntriesIds,
  annotationsSelectors.getAnnotationFlow,
  contextsSelectors.getHasSelectedContextFromAll,
  contextsSelectors.getSelectedContextFromAll,
  (
    testSet,
    testSetEntriesIds,
    annotationFlow,
    hasSelectedContext,
    context,
  ) => ({
    test_set_id: testSet.id,
    test_set_entries: testSetEntriesIds,
    annotation_flow: annotationFlow,
    context: hasSelectedContext ? context.id : null,
  }),
);
